import { useCanAccess, useGetIdentity, useGetList } from "ra-core";
import { useMemo } from "react";

import type { Scoring } from "../../prospects/prospectTypes";
import { addDaysKey, osloDateKey } from "../../misc/osloDate";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type {
  Company,
  CompanyNextStep,
  CompanyNote,
  CompanyProduct,
  Deal,
  MiminNotification,
  Product,
  WorkItem,
} from "../../types";
import { useLeadAttention } from "../useLeadAttention";
import { buildRows, type DriftScan, type LastContactRow } from "./buildRows";
import { QUIET_DAYS, type FocusRow } from "./ranking";

// Henter alt «Gjør nå» trenger, for hele teamet. Mine, Team og Uten eier er
// filtre på klienten, så et bytte av visning henter ingenting på nytt.
// Grensene er vern mot en løpsk spørring, ikke produktvalg.
const LIMIT = 500;

type ScanRow = {
  id: number;
  org_nr: string;
  scanned_at: string;
  scoring: Scoring | null;
};

const NONE: never[] = [];

export const useFocusData = () => {
  const { identity, isPending: identityPending } = useGetIdentity();
  const { canAccess: isAdmin = false } = useCanAccess({
    resource: "sales",
    action: "list",
  });
  const { dealStages } = useConfigurationContext();
  const today = osloDateKey();
  const enabled = !identityPending;
  const quietBefore = addDaysKey(today, -QUIET_DAYS);

  const tasks = useGetList<WorkItem>(
    "work_items",
    {
      filter: { "completed_at@is": null },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: LIMIT },
    },
    { enabled },
  );
  const steps = useGetList<CompanyNextStep>(
    "company_next_steps",
    {
      filter: { "completed_at@is": null },
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: LIMIT },
    },
    { enabled },
  );
  const notes = useGetList<CompanyNote>(
    "company_notes",
    {
      filter: { "follow_up_date@lte": today },
      sort: { field: "follow_up_date", order: "ASC" },
      pagination: { page: 1, perPage: LIMIT },
    },
    { enabled },
  );
  const deals = useGetList<Deal>(
    "deals",
    {
      filter: { "archived_at@is": null, status: "open" },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: LIMIT },
    },
    { enabled },
  );
  const renewals = useGetList<Deal>(
    "deals",
    {
      filter: {
        "archived_at@is": null,
        "renewal_date@lte": addDaysKey(today, 60),
      },
      sort: { field: "renewal_date", order: "ASC" },
      pagination: { page: 1, perPage: LIMIT },
    },
    { enabled },
  );
  const lead = useLeadAttention(enabled);
  const companies = useGetList<Company>(
    "companies",
    {
      filter: { "archived_at@is": null },
      sort: { field: "name", order: "ASC" },
      pagination: { page: 1, perPage: 2000 },
    },
    { enabled },
  );
  const lastContacts = useGetList<LastContactRow & { id: number }>(
    "company_last_contact",
    {
      filter: { "last_contact_sort@lt": quietBefore },
      sort: { field: "last_contact_sort", order: "ASC" },
      pagination: { page: 1, perPage: 2000 },
    },
    { enabled },
  );
  const agreements = useGetList<CompanyProduct>(
    "company_products",
    {
      filter: { "billing_interval@neq": "one_time" },
      sort: { field: "id", order: "ASC" },
      pagination: { page: 1, perPage: 2000 },
    },
    { enabled: enabled && isAdmin },
  );
  const products = useGetList<Product>(
    "products",
    {
      sort: { field: "name", order: "ASC" },
      pagination: { page: 1, perPage: 2000 },
    },
    { enabled: enabled && isAdmin },
  );
  const notifications = useGetList<MiminNotification>(
    "notifications",
    {
      filter: { recipient_id: identity?.id, "read_at@is": null },
      sort: { field: "created_at", order: "DESC" },
      pagination: { page: 1, perPage: 50 },
    },
    { enabled: enabled && identity?.id != null },
  );

  // Kundeskannet (Driftsradar): siste lette skann per org.nr.
  const companyList = companies.data ?? NONE;
  const orgNrs = useMemo(
    () =>
      companyList
        .filter((c) => !c.is_own_company)
        .map((c) => c.tax_identifier?.replace(/\s/g, "") ?? "")
        .filter((nr) => /^\d{9}$/.test(nr)),
    [companyList],
  );
  const scans = useGetList<ScanRow>(
    "entity_scans",
    {
      filter: { "org_nr@in": `(${orgNrs.join(",")})`, scan_type: "light" },
      sort: { field: "scanned_at", order: "DESC" },
      pagination: { page: 1, perPage: 2000 },
    },
    { enabled: enabled && orgNrs.length > 0 },
  );

  const drift = useMemo((): DriftScan[] => {
    const byOrg = new Map(
      companyList.map((c) => [c.tax_identifier?.replace(/\s/g, ""), c]),
    );
    const seen = new Set<string>();
    const out: DriftScan[] = [];
    for (const scan of scans.data ?? NONE) {
      if (seen.has(scan.org_nr)) continue;
      seen.add(scan.org_nr);
      const company = byOrg.get(scan.org_nr);
      const findings = (scan.scoring?.smerte ?? []).map((s) => s.t);
      if (company && findings.length) {
        out.push({
          companyId: company.id,
          findings,
          scannedAt: scan.scanned_at,
        });
      }
    }
    return out;
  }, [scans.data, companyList]);

  const sources = [
    tasks,
    steps,
    notes,
    deals,
    renewals,
    companies,
    lastContacts,
    notifications,
  ];
  const isPending =
    identityPending ||
    sources.some((s) => s.isPending) ||
    lead.isPending ||
    (isAdmin && (agreements.isPending || products.isPending));
  const isError =
    sources.some((s) => s.isError) ||
    (isAdmin && (agreements.isError || products.isError));

  // ra-core legger endrede poster tilbake i bufrede lister uten å hente på
  // nytt (en fullført oppgave blir liggende med completed_at satt), så
  // filtrene gjentas på klienten i buildRows.
  const rows = useMemo(
    (): FocusRow[] =>
      isPending
        ? []
        : buildRows({
            today,
            tasks: (tasks.data ?? NONE).filter((t) => !t.completed_at),
            customerSteps: steps.data ?? NONE,
            followUpNotes: (notes.data ?? NONE).filter(
              (n) => !!n.follow_up_date && n.follow_up_date <= today,
            ),
            openDeals: (deals.data ?? NONE).filter((d) => d.status === "open"),
            renewalDeals: renewals.data ?? NONE,
            lead,
            companies: companyList,
            lastContacts: lastContacts.data ?? NONE,
            agreements: agreements.data ?? NONE,
            products: products.data ?? NONE,
            drift,
            notifications: (notifications.data ?? NONE).filter(
              (n) => !n.read_at,
            ),
            dealStages,
            isAdmin,
          }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isPending,
      today,
      tasks.data,
      steps.data,
      notes.data,
      deals.data,
      renewals.data,
      lead.unowned,
      lead.noContact,
      lead.parseFailed,
      companyList,
      lastContacts.data,
      agreements.data,
      products.data,
      drift,
      notifications.data,
      dealStages,
      isAdmin,
    ],
  );

  const refetch = () => {
    for (const s of [...sources, agreements, products, scans]) {
      void s.refetch();
    }
  };

  return {
    rows,
    isPending,
    isError,
    refetch,
    isAdmin,
    me: identity?.id ?? null,
    today,
    companies: companyList,
    openDeals: deals.data ?? NONE,
    tasks: tasks.data ?? NONE,
    customerSteps: (steps.data ?? NONE).filter((s) => !s.completed_at),
    drift,
    hasAnyData:
      companyList.some((c) => !c.is_own_company) ||
      (deals.data ?? NONE).length > 0,
  };
};
